/**
 * My truck — the tech's van as a Spire warehouse.
 *
 * Stock is read live from Spire (nothing cached beyond TanStack Query).
 * Two actions:
 *   - Transfer: restock from another warehouse into the van, or hand stock
 *     to another truck. Created AND posted in Spire on submit (agreed
 *     2026-09-25) — the numbers move immediately.
 *   - Count: walk the van, enter what's actually there. Submitted for the
 *     office to review; approving posts a Spire adjustment. The tech never
 *     changes inventory value directly.
 *
 * Both need a signal — this screen is not offline-capable (a transfer is
 * a Spire document, not a queued field event). The outbox is not involved.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowLeftRight,
  ClipboardCheck,
  Loader2,
  RefreshCw,
  ScanLine,
  Search,
  Truck,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ScanModal } from '../components/scan-modal.js';
import { Button } from '../components/ui/button.js';
import { Feedback } from '../components/ui/feedback.js';
import { Input } from '../components/ui/input.js';
import { Pill } from '../components/ui/pill.js';
import { QueryError } from '../components/ui/query-error.js';
import {
  ApiError,
  api,
  type CreateTransferBody,
  type InventoryItemSummary,
  type StockMove,
  type TruckCount,
  type TruckStockLine,
  type Warehouse,
} from '../lib/api.js';
import { cn } from '../lib/cn.js';
import { useDialogA11y } from '../lib/use-dialog-a11y.js';

export const MY_TRUCK_KEY = ['trucks', 'mine'] as const;

export function TruckScreen() {
  const [q, setQ] = useState('');
  const [sheet, setSheet] = useState<'transfer' | 'count' | null>(null);
  const mine = useQuery({
    queryKey: MY_TRUCK_KEY,
    queryFn: ({ signal }) => api.trucks.mine(undefined, signal),
    staleTime: 30_000,
  });
  const truck = mine.data?.truck ?? null;
  const stock = mine.data?.stock ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return stock;
    return stock.filter((s) => s.partNo.toLowerCase().includes(needle) || s.description.toLowerCase().includes(needle));
  }, [stock, q]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Link to="/me" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Me
        </Link>
        <button
          type="button"
          onClick={() => void mine.refetch()}
          disabled={mine.isFetching}
          aria-label="Refresh stock"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', mine.isFetching && 'animate-spin')} aria-hidden />
        </button>
      </div>

      <header>
        <div className="text-xs text-ink-muted inline-flex items-center gap-1.5">
          <Truck className="h-3.5 w-3.5" aria-hidden /> My truck
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          {truck ? (
            <>
              <span className="font-mono">{truck.whse}</span>
              {truck.whseDescription ? <span className="text-ink-muted font-normal text-lg"> · {truck.whseDescription}</span> : null}
            </>
          ) : (
            'Truck stock'
          )}
        </h1>
      </header>

      {mine.isError && (
        <QueryError
          title={mine.error.status === 400 ? 'No truck set up' : "Couldn't load your truck"}
          error={mine.error}
          onRetry={() => void mine.refetch()}
        />
      )}

      {truck && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" onClick={() => setSheet('transfer')} className="h-12">
              <ArrowLeftRight className="h-4 w-4 mr-1.5" aria-hidden /> Transfer
            </Button>
            <Button variant="secondary" onClick={() => setSheet('count')} className="h-12">
              <ClipboardCheck className="h-4 w-4 mr-1.5" aria-hidden /> Count my truck
            </Button>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" aria-hidden />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${stock.length} items on the truck`}
              className="pl-9"
              aria-label="Search truck stock"
            />
          </div>

          <StockList lines={filtered} loading={mine.isPending} emptyText={q ? 'Nothing on the truck matches.' : 'Nothing in stock on this truck.'} />

          <RecentTransfers whse={truck.whse} />
          <MyCounts />
        </>
      )}

      {sheet === 'transfer' && truck && (
        <TransferSheet myWhse={truck.whse} stock={stock} onClose={() => setSheet(null)} />
      )}
      {sheet === 'count' && truck && <CountSheet stock={stock} onClose={() => setSheet(null)} />}
    </div>
  );
}

// ─── Stock list ────────────────────────────────────────────────────────────

function StockList({ lines, loading, emptyText }: { lines: TruckStockLine[]; loading: boolean; emptyText: string }) {
  if (loading) {
    return (
      <div className="space-y-2" aria-busy>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-12 rounded-md animate-pulse" style={{ backgroundColor: 'var(--color-background-tertiary)' }} />
        ))}
      </div>
    );
  }
  if (lines.length === 0) return <div className="text-sm text-ink-muted py-6 text-center">{emptyText}</div>;
  return (
    <ul className="divide-y rounded-lg border bg-surface" style={{ borderColor: 'var(--color-border-tertiary)' }}>
      {lines.map((l) => (
        <li key={l.inventoryId} className="flex items-center gap-3 px-3 py-2">
          <div className="min-w-0 flex-1 leading-tight">
            <div className="font-mono text-sm text-ink truncate">{l.partNo}</div>
            <div className="text-xs text-ink-muted truncate">{l.description}</div>
          </div>
          <div className="text-right leading-tight shrink-0">
            <div className="text-sm font-semibold tabular-nums text-ink">
              {l.onHand}
              {l.uom ? <span className="text-xs font-normal text-ink-muted"> {l.uom}</span> : null}
            </div>
            {l.committed !== '0' && <div className="text-[11px] text-ink-subtle tabular-nums">{l.available} free</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Recent transfers ──────────────────────────────────────────────────────

function RecentTransfers({ whse }: { whse: string }) {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ['trucks', whse, 'transfers'],
    queryFn: ({ signal }) => api.trucks.transfers(whse, signal),
    enabled: open,
    staleTime: 60_000,
  });
  return (
    <section className="rounded-lg border bg-surface" style={{ borderColor: 'var(--color-border-tertiary)' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full flex items-center justify-between px-3 py-2 text-sm text-ink">
        <span className="inline-flex items-center gap-1.5">
          <ArrowLeftRight className="h-4 w-4 text-ink-muted" aria-hidden /> Recent transfers
        </span>
        <span className="text-xs text-ink-muted">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="border-t px-3 py-2" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          {query.isPending && <div className="text-xs text-ink-muted">Loading…</div>}
          {query.isError && <Feedback variant="error" size="sm">{query.error.message}</Feedback>}
          {query.data && query.data.length === 0 && <div className="text-xs text-ink-muted">No transfers yet.</div>}
          <ul className="space-y-1 text-sm">
            {(query.data ?? []).map((t) => (
              <TransferRow key={t.spireId} t={t} whse={whse} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function TransferRow({ t, whse }: { t: StockMove; whse: string }) {
  const incoming = t.destinationWhse === whse;
  return (
    <li className="flex items-center gap-2">
      <Pill variant={t.posted ? (incoming ? 'success' : 'info') : 'warning'}>{incoming ? 'In' : 'Out'}</Pill>
      <span className="font-mono text-xs text-ink">{t.number}</span>
      <span className="text-xs text-ink-muted truncate">
        {t.sourceWhse} → {t.destinationWhse}
        {t.referenceNo ? ` · ${t.referenceNo}` : ''}
      </span>
      <span className="ml-auto text-xs text-ink-subtle tabular-nums">{t.date}</span>
      {!t.posted && <span className="text-[10px] text-amber-700">unposted</span>}
    </li>
  );
}

// ─── My counts ─────────────────────────────────────────────────────────────

function MyCounts() {
  const query = useQuery({
    queryKey: ['trucks', 'counts', 'mine'],
    queryFn: ({ signal }) => api.trucks.counts(undefined, signal),
    staleTime: 30_000,
  });
  const counts = query.data ?? [];
  if (counts.length === 0) return null;
  return (
    <section className="rounded-lg border bg-surface px-3 py-2" style={{ borderColor: 'var(--color-border-tertiary)' }}>
      <div className="text-sm text-ink inline-flex items-center gap-1.5 mb-1">
        <ClipboardCheck className="h-4 w-4 text-ink-muted" aria-hidden /> My counts
      </div>
      <ul className="space-y-1 text-sm">
        {counts.slice(0, 5).map((c) => (
          <li key={c.id} className="flex items-center gap-2">
            <CountStatusPill c={c} />
            <span className="text-xs text-ink-muted">
              {c.lineCount} lines, {c.varianceCount} off
              {c.spireAdjustmentNo ? ` · adj ${c.spireAdjustmentNo}` : ''}
              {c.status === 'rejected' && c.reviewNote ? ` · ${c.reviewNote}` : ''}
            </span>
            <span className="ml-auto text-xs text-ink-subtle tabular-nums">{c.submittedAt.slice(0, 10)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CountStatusPill({ c }: { c: TruckCount }) {
  if (c.status === 'approved') return <Pill variant="success" dot>Approved</Pill>;
  if (c.status === 'rejected') return <Pill variant="danger">Rejected</Pill>;
  return <Pill variant="warning" dot>Awaiting review</Pill>;
}

// ─── Transfer sheet ────────────────────────────────────────────────────────

type TransferLine = { partNo: string; description: string; qty: string; uom: string | null; available: string | null };

function TransferSheet({
  myWhse,
  stock,
  onClose,
}: {
  myWhse: string;
  stock: TruckStockLine[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose);
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [other, setOther] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<TransferLine[]>([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [done, setDone] = useState<StockMove | null>(null);
  // One key per sheet instance: a retry after a timeout replays as the same document.
  const [idemKey] = useState(() => crypto.randomUUID());

  const warehouses = useWarehouseOptions(myWhse);
  const sourceWhse = direction === 'in' ? other : myWhse;
  const destinationWhse = direction === 'in' ? myWhse : other;

  const submit = useMutation<StockMove, ApiError, CreateTransferBody>({
    mutationFn: (body) => api.trucks.transfer(body, idemKey),
    onSuccess: (res) => {
      setDone(res);
      void queryClient.invalidateQueries({ queryKey: MY_TRUCK_KEY });
      void queryClient.invalidateQueries({ queryKey: ['trucks'] });
    },
  });

  const addLine = (item: { partNo: string; description: string; uom: string | null; available: string | null }) => {
    setLines((prev) => {
      if (prev.some((l) => l.partNo === item.partNo)) return prev;
      return [...prev, { ...item, qty: '1' }];
    });
  };
  const canSubmit = !!other && other !== myWhse && lines.length > 0 && lines.every((l) => Number(l.qty) > 0) && !submit.isPending;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    submit.mutate({
      sourceWhse,
      destinationWhse,
      ...(reference.trim() ? { referenceNo: reference.trim().slice(0, 30) } : {}),
      items: lines.map((l) => ({ partNo: l.partNo, qty: l.qty, ...(l.uom ? { uom: l.uom } : {}) })),
    });
  };

  return (
    <SheetShell title={done ? 'Transfer posted' : 'Transfer stock'} onClose={onClose} dialogRef={dialogRef}>
      {done ? (
        <div className="space-y-3">
          <Feedback variant="success" size="lg">
            <span className="font-mono">{done.number}</span> posted in Spire — {done.items.length} line{done.items.length === 1 ? '' : 's'}{' '}
            {done.sourceWhse} → {done.destinationWhse}.
          </Feedback>
          <Button onClick={onClose} className="w-full">Done</Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-1 rounded-md border p-1" style={{ borderColor: 'var(--color-border-tertiary)' }}>
            {(['in', 'out'] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => { setDirection(d); setLines([]); }}
                aria-pressed={direction === d}
                className={cn('h-9 rounded text-sm', direction === d ? 'bg-brand-50 text-ink-accent font-medium' : 'text-ink-muted')}
              >
                {d === 'in' ? `Into my truck (${myWhse})` : `Out of my truck (${myWhse})`}
              </button>
            ))}
          </div>

          <label className="block text-sm space-y-1">
            <span className="text-xs uppercase tracking-wider text-ink-subtle">{direction === 'in' ? 'From warehouse' : 'To warehouse'}</span>
            <select
              value={other}
              onChange={(e) => { setOther(e.target.value); setLines([]); }}
              required
              className="h-11 w-full rounded-lg border bg-surface px-3 text-base"
              style={{ borderColor: 'var(--color-border-secondary)' }}
            >
              <option value="">Choose…</option>
              {warehouses.map((w) => (
                <option key={w.code} value={w.code}>
                  {w.code}{w.description ? ` — ${w.description}` : ''}
                </option>
              ))}
            </select>
          </label>

          {sourceWhse && (
            <ItemFinder
              whse={sourceWhse}
              hint={direction === 'in' ? `Search ${sourceWhse} for what you need` : 'Pick from your truck'}
              localStock={direction === 'out' ? stock : null}
              onPick={addLine}
              onScan={() => setScanOpen(true)}
            />
          )}

          {lines.length > 0 && (
            <ul className="divide-y rounded-md border" style={{ borderColor: 'var(--color-border-tertiary)' }}>
              {lines.map((l) => (
                <li key={l.partNo} className="flex items-center gap-2 px-2 py-1.5">
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="font-mono text-sm text-ink truncate">{l.partNo}</div>
                    <div className="text-[11px] text-ink-muted truncate">
                      {l.description}
                      {l.available !== null ? ` · ${l.available} available` : ''}
                    </div>
                  </div>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="0.0001"
                    step="any"
                    value={l.qty}
                    onChange={(e) => setLines((prev) => prev.map((x) => (x.partNo === l.partNo ? { ...x, qty: e.target.value } : x)))}
                    className="h-9 w-20 text-right font-mono"
                    aria-label={`Quantity for ${l.partNo}`}
                  />
                  <button type="button" onClick={() => setLines((prev) => prev.filter((x) => x.partNo !== l.partNo))} aria-label={`Remove ${l.partNo}`} className="text-ink-muted hover:text-ink">
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference (optional, e.g. job 0000125291)" maxLength={30} />

          {submit.isError && <Feedback variant="error" size="sm">{submit.error.message}</Feedback>}

          <Button type="submit" disabled={!canSubmit} className="w-full h-12">
            {submit.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" aria-hidden /> Posting to Spire…</> : `Transfer ${lines.length || ''} ${lines.length === 1 ? 'item' : 'items'}`}
          </Button>
          <p className="text-[11px] text-ink-subtle">Posts immediately — stock moves in Spire as soon as you submit. Made a mistake? Do the reverse transfer.</p>
        </form>
      )}
      <ScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={({ text }) => {
          setScanOpen(false);
          void api.inventory.search({ partNo: text, ...(sourceWhse ? { whse: sourceWhse } : {}), limit: 1 }).then((hits) => {
            const h = hits[0];
            if (h) addLine({ partNo: h.partNo, description: h.description ?? '', uom: h.uom, available: h.availableQty });
          });
        }}
      />
    </SheetShell>
  );
}

/** Warehouses a tech may transfer against: every active one except their own. */
function useWarehouseOptions(myWhse: string): Array<Pick<Warehouse, 'code' | 'description' | 'status'>> {
  const query = useQuery({
    queryKey: ['trucks', 'warehouses'],
    queryFn: ({ signal }) => api.trucks.warehouses(signal),
    staleTime: 5 * 60_000,
  });
  return (query.data ?? []).filter((w) => w.code !== myWhse && w.status === 'active');
}

function ItemFinder({
  whse,
  hint,
  localStock,
  onPick,
  onScan,
}: {
  whse: string;
  hint: string;
  localStock: TruckStockLine[] | null;
  onPick: (item: { partNo: string; description: string; uom: string | null; available: string | null }) => void;
  onScan: () => void;
}) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  const remote = useQuery({
    queryKey: ['inventory', 'search', whse, debounced],
    queryFn: ({ signal }) => api.inventory.search({ whse, q: debounced, limit: 15 }, signal),
    enabled: localStock === null && debounced.length >= 2,
    staleTime: 30_000,
  });
  const hits: Array<{ partNo: string; description: string; uom: string | null; available: string | null }> = useMemo(() => {
    if (localStock) {
      const needle = debounced.toLowerCase();
      return localStock
        .filter((s) => !needle || s.partNo.toLowerCase().includes(needle) || s.description.toLowerCase().includes(needle))
        .slice(0, 15)
        .map((s) => ({ partNo: s.partNo, description: s.description, uom: s.uom, available: s.available }));
    }
    return (remote.data ?? []).map((i: InventoryItemSummary) => ({ partNo: i.partNo, description: i.description ?? '', uom: i.uom, available: i.availableQty }));
  }, [localStock, remote.data, debounced]);

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={hint} className="pl-9" aria-label="Find item" />
        </div>
        <Button type="button" variant="secondary" onClick={onScan} aria-label="Scan barcode" className="h-11 w-11 p-0">
          <ScanLine className="h-5 w-5" aria-hidden />
        </Button>
      </div>
      {(debounced.length >= 2 || localStock) && hits.length > 0 && (
        <ul className="max-h-48 overflow-y-auto rounded-md border divide-y" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          {hits.map((h) => (
            <li key={h.partNo}>
              <button type="button" onClick={() => { onPick(h); setQ(''); }} className="w-full text-left px-2 py-1.5 hover:bg-surface-muted">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-ink truncate">{h.partNo}</span>
                  <span className="ml-auto text-xs text-ink-muted tabular-nums shrink-0">{h.available ?? '—'}{h.uom ? ` ${h.uom}` : ''}</span>
                </div>
                <div className="text-[11px] text-ink-muted truncate">{h.description}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {remote.isFetching && <div className="text-[11px] text-ink-subtle">Searching {whse}…</div>}
    </div>
  );
}

// ─── Count sheet ───────────────────────────────────────────────────────────

type CountRow = { partNo: string; description: string; uom: string | null; expected: string; counted: string };

function CountSheet({ stock, onClose }: { stock: TruckStockLine[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose);
  const [rows, setRows] = useState<CountRow[]>(() =>
    stock.map((s) => ({ partNo: s.partNo, description: s.description, uom: s.uom, expected: s.onHand, counted: '' })),
  );
  const [q, setQ] = useState('');
  const [extra, setExtra] = useState('');
  const [note, setNote] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [done, setDone] = useState<TruckCount | null>(null);

  const submit = useMutation<TruckCount, ApiError, void>({
    mutationFn: () =>
      api.trucks.submitCount({
        ...(note.trim() ? { note: note.trim() } : {}),
        lines: rows.filter((r) => r.counted !== '').map((r) => ({ partNo: r.partNo, countedQty: r.counted })),
      }),
    onSuccess: (res) => {
      setDone(res);
      void queryClient.invalidateQueries({ queryKey: ['trucks', 'counts'] });
    },
  });

  const entered = rows.filter((r) => r.counted !== '');
  const variances = entered.filter((r) => Number(r.counted) !== Number(r.expected));
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? rows.filter((r) => r.partNo.toLowerCase().includes(needle) || r.description.toLowerCase().includes(needle)) : rows;
  }, [rows, q]);

  const addExtra = (partNo: string, description: string, uom: string | null) => {
    setRows((prev) => (prev.some((r) => r.partNo === partNo) ? prev : [{ partNo, description, uom, expected: '0', counted: '1' }, ...prev]));
    setExtra('');
  };
  const focusScanned = (partNo: string) => {
    setQ(partNo);
  };

  return (
    <SheetShell title={done ? 'Count submitted' : 'Count my truck'} onClose={onClose} dialogRef={dialogRef}>
      {done ? (
        <div className="space-y-3">
          <Feedback variant="success" size="lg">
            Sent to the office for review — {done.lineCount} line{done.lineCount === 1 ? '' : 's'}, {done.varianceCount} with a difference from Spire.
            {done.varianceCount > 0 ? ' Stock changes once they approve it.' : ' Nothing to adjust.'}
          </Feedback>
          <Button onClick={onClose} className="w-full">Done</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-ink-muted">
            Enter what's actually on the truck. Leave a line blank to skip it (a partial count is fine). Found something Spire doesn't list? Add it below.
          </p>
          <div className="flex gap-1.5">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle" aria-hidden />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jump to an item" className="pl-9" aria-label="Filter count list" />
            </div>
            <Button type="button" variant="secondary" onClick={() => setScanOpen(true)} aria-label="Scan barcode" className="h-11 w-11 p-0">
              <ScanLine className="h-5 w-5" aria-hidden />
            </Button>
          </div>

          <ul className="max-h-[45vh] overflow-y-auto divide-y rounded-md border" style={{ borderColor: 'var(--color-border-tertiary)' }}>
            {visible.map((r) => {
              const off = r.counted !== '' && Number(r.counted) !== Number(r.expected);
              return (
                <li key={r.partNo} className={cn('flex items-center gap-2 px-2 py-1.5', off && 'bg-amber-50')}>
                  <div className="min-w-0 flex-1 leading-tight">
                    <div className="font-mono text-sm text-ink truncate">{r.partNo}</div>
                    <div className="text-[11px] text-ink-muted truncate">{r.description}</div>
                  </div>
                  <div className="text-[11px] text-ink-subtle tabular-nums shrink-0" title="Spire on-hand">
                    Spire {r.expected}
                  </div>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    value={r.counted}
                    placeholder="—"
                    onChange={(e) => setRows((prev) => prev.map((x) => (x.partNo === r.partNo ? { ...x, counted: e.target.value } : x)))}
                    className={cn('h-9 w-20 text-right font-mono', off && 'border-amber-400')}
                    aria-label={`Counted quantity for ${r.partNo}`}
                  />
                </li>
              );
            })}
            {visible.length === 0 && <li className="px-2 py-3 text-xs text-ink-muted">No items match.</li>}
          </ul>

          <ExtraItemFinder value={extra} onChange={setExtra} onPick={addExtra} />

          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the office (optional)" maxLength={500} />

          <div className="text-xs text-ink-muted">
            {entered.length} counted · {variances.length} differ from Spire
          </div>
          {submit.isError && <Feedback variant="error" size="sm">{submit.error.message}</Feedback>}
          <Button onClick={() => submit.mutate()} disabled={entered.length === 0 || submit.isPending} className="w-full h-12">
            {submit.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" aria-hidden /> Submitting…</> : 'Submit for review'}
          </Button>
        </div>
      )}
      <ScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={({ text }) => {
          setScanOpen(false);
          const hit = rows.find((r) => r.partNo.toLowerCase() === text.trim().toLowerCase());
          if (hit) {
            focusScanned(hit.partNo);
            return;
          }
          void api.inventory.search({ partNo: text, limit: 1 }).then((hits) => {
            const h = hits[0];
            if (h) addExtra(h.partNo, h.description ?? '', h.uom);
          });
        }}
      />
    </SheetShell>
  );
}

function ExtraItemFinder({ value, onChange, onPick }: { value: string; onChange: (v: string) => void; onPick: (partNo: string, description: string, uom: string | null) => void }) {
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value.trim()), 250);
    return () => clearTimeout(h);
  }, [value]);
  const query = useQuery({
    queryKey: ['inventory', 'search', 'any', debounced],
    queryFn: ({ signal }) => api.inventory.search({ q: debounced, limit: 8 }, signal),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });
  return (
    <div className="space-y-1">
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Add an item Spire doesn't list on the truck…" aria-label="Add item to count" />
      {debounced.length >= 2 && (query.data ?? []).length > 0 && (
        <ul className="max-h-36 overflow-y-auto rounded-md border divide-y" style={{ borderColor: 'var(--color-border-tertiary)' }}>
          {(query.data ?? []).map((i) => (
            <li key={`${i.whseCode}-${i.partNo}`}>
              <button type="button" onClick={() => onPick(i.partNo, i.description ?? '', i.uom)} className="w-full text-left px-2 py-1.5 hover:bg-surface-muted">
                <span className="font-mono text-sm text-ink">{i.partNo}</span>
                <span className="text-[11px] text-ink-muted"> · {i.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Shell ─────────────────────────────────────────────────────────────────

function SheetShell({
  title,
  onClose,
  dialogRef,
  children,
}: {
  title: string;
  onClose: () => void;
  dialogRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-40 bg-slate-950/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-xl shadow-xl"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <header className="sticky top-0 flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border-tertiary)', backgroundColor: 'var(--color-elevated-surface)' }}>
          <span className="text-sm font-medium text-ink">{title}</span>
          <button type="button" onClick={onClose} aria-label="Close" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
